////? uncomment the following code to restore the PickUpTab component
import { FieldArray, FormikErrors, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  TabPane,
} from 'reactstrap';
import { CustomerFormType, PickupAddress } from 'types/customer/customerTypes';
import Select from 'react-select';
import { CustomReactSelectStyles } from 'styles/common/singleSelectStyle';
import { RootState } from 'slices';
import { useSelector } from 'react-redux';

import { useState, useCallback } from 'react';
import {
  getDistricts,
  getProvinces,
  getSubDistricts,
} from 'services/locationService';
import {
  mapLocationsToOptions,
  mapProvincesToOptions,
  mapSubDistrictToOptions,
} from 'utils/mappers/locationMapper';

import { SelectedData } from 'types/common/FormTypes';
import { useDispatch } from 'react-redux';
import {
  fetchProvincesShippingFailure,
  fetchProvincesShippingStart,
  fetchProvincesShippingSuccess,
  selectProvincesShippingStatus,
} from 'slices/locations/reducer';
import 'styles/common/section-modal.css';

interface PickupLocationOptions {
  districts: SelectedData[];
  subDistricts: SelectedData[];
}

const PickUpTab = ({
  formik,
  handleDeletePickupAddr,
}: {
  formik: FormikProps<CustomerFormType>;
  handleDeletePickupAddr: (option: PickupAddress) => void;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;

  const locationsState = useSelector((state: RootState) => state.Locations);
  const { ProvincesShippingOptions, provincesError } = locationsState;
  const provincesShippingStatus = useSelector(selectProvincesShippingStatus);

  // const customerState = useSelector((state: RootState) => state.Customer);
  // const { isEdit } = customerState;

  const LabelClass = 'col-12 col-sm-auto col-form-label label-fixed';
  const ColClass = 'col-fixed-2';
  const RowClass = 'row-fixed-2';

  const fetchProvinces = useCallback(async () => {
    dispatch(fetchProvincesShippingStart());
    try {
      const response = await getProvinces();
      const { message, error_code, data } = response ?? {};
      if (message === 'OK' && error_code === 0) {
        const provincesOptions = data?.provinces
          ? await mapProvincesToOptions(data.provinces)
          : [];

        dispatch(fetchProvincesShippingSuccess(provincesOptions));
      } else {
        dispatch(fetchProvincesShippingFailure(t('Invalid response')));
      }
    } catch (err) {
      console.error('Failed to fetch provinces:', err);
      dispatch(fetchProvincesShippingFailure(t('Failed to load')));
    }
  }, [dispatch, t]);

  useEffect(() => {
    if (provincesShippingStatus === 'idle') {
      fetchProvinces();
    }
  }, [provincesShippingStatus, fetchProvinces]);

  const [locationOptions, setLocationOptions] = useState<
    Record<number, PickupLocationOptions>
  >({});

  const loadDistricts = useCallback(
    async (index: number, provinceId: string) => {
      if (!provinceId) return;

      try {
        const response = await getDistricts(Number(provinceId));
        if (response.message === 'OK') {
          const districts = await mapLocationsToOptions(
            response.data.districts
          );

          setLocationOptions((prev) => ({
            ...prev,
            [index]: {
              subDistricts: [], // reset subdistrict on province change
              districts,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to load districts', err);
      }
    },
    []
  );

  const loadSubDistricts = useCallback(
    async (index: number, districtId: string) => {
      if (!districtId) return;

      try {
        const response = await getSubDistricts(Number(districtId));
        if (response.message === 'OK') {
          const subDistricts = await mapSubDistrictToOptions(
            response.data.subdistricts
          );

          setLocationOptions((prev) => ({
            ...prev,
            [index]: {
              ...(prev[index] || { districts: [] }),
              subDistricts,
            },
          }));
        }
      } catch (err) {
        console.error('Failed to load subdistricts', err);
      }
    },
    []
  );

  const getOpt = (opts: any[], value?: string) =>
    value ? (opts.find((o) => o.value === value) ?? null) : null;

  useEffect(() => {
    formik.values.pickupAddresses.forEach((pickup, index) => {
      // load districts if state exists and not loaded yet
      if (
        pickup.state &&
        (!locationOptions[index] ||
          locationOptions[index].districts.length === 0)
      ) {
        loadDistricts(index, pickup.state);
      }

      // load subdistricts if district exists and not loaded yet
      if (
        pickup.district &&
        locationOptions[index]?.districts?.length > 0 &&
        (!locationOptions[index]?.subDistricts ||
          locationOptions[index].subDistricts.length === 0)
      ) {
        loadSubDistricts(index, pickup.district);
      }
    });
  }, [
    formik.values.pickupAddresses,
    locationOptions,
    loadDistricts,
    loadSubDistricts,
  ]);

  const isDarkMode = layoutModeType === 'dark';

  return (
    <TabPane tabId="pickup">
      <FieldArray
        name="pickupAddresses"
        render={(arrayHelpers) => (
          <>
            {formik.values.pickupAddresses.map((pickup, index) => {
              const isDefault = pickup.isDefault;
              return (
                <Card
                  key={index}
                  className="ribbon-box border shadow-none mb-lg-2"
                >
                  <CardBody>
                    {isDefault && (
                      <div
                        className="ribbon ribbon-info ribbon-shape"
                        style={{ fontSize: '0.8125rem' }}
                      >
                        <span>{t('Default')}</span>
                      </div>
                    )}

                    <Row className="mt-4 mb-3">
                      <Col lg={6}>
                        <Row>
                          <Label className="col-sm-3 col-form-label">
                            {t('Pickup Name')}{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Label>

                          <Col sm={9}>
                            <Input
                              name={`pickupAddresses[${index}].pickupName`}
                              value={pickup.pickupName}
                              onChange={formik.handleChange}
                            />
                            {formik.touched.pickupAddresses?.[index]
                              ?.pickupName &&
                              typeof formik.errors.pickupAddresses?.[index] ===
                                'object' &&
                              (
                                formik.errors.pickupAddresses?.[
                                  index
                                ] as FormikErrors<PickupAddress>
                              )?.phone && (
                                <div className="text-danger">
                                  {
                                    (
                                      formik.errors.pickupAddresses?.[
                                        index
                                      ] as FormikErrors<PickupAddress>
                                    ).pickupName
                                  }
                                </div>
                              )}
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={6}>
                        <Row className={`${RowClass}`}>
                          <Label className="col-sm-3 col-form-label">
                            {t('Phone Number')}{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </Label>
                          <Col sm={9}>
                            <Input
                              type="tel"
                              name={`pickupAddresses[${index}].phone`}
                              value={pickup.phone}
                              onChange={formik.handleChange}
                              maxLength={20}
                            />
                            {formik.touched.pickupAddresses?.[index]?.phone &&
                              typeof formik.errors.pickupAddresses?.[index] ===
                                'object' &&
                              (
                                formik.errors.pickupAddresses?.[
                                  index
                                ] as FormikErrors<PickupAddress>
                              )?.phone && (
                                <div className="text-danger">
                                  {
                                    (
                                      formik.errors.pickupAddresses?.[
                                        index
                                      ] as FormikErrors<PickupAddress>
                                    ).phone
                                  }
                                </div>
                              )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Label className={`${LabelClass}`}>{t('Address')}</Label>
                      <Col className={`${ColClass}`}>
                        <Input
                          name={`pickupAddresses[${index}].address`}
                          // placeholder="ระบุที่อยู่ (บ้านเลขที่ ถนน ซอย)"
                          value={pickup.address}
                          onChange={formik.handleChange}
                        />
                        <div
                          className="text-muted pt-1"
                          style={{ fontSize: '0.75rem' }}
                        >
                          {t('NewAddressDescription')}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col lg={6}>
                        <Row>
                          <Label className="col-sm-3 col-form-label">
                            {t('Province')}
                          </Label>

                          <Col sm={9}>
                            <Select
                              styles={CustomReactSelectStyles(isDarkMode)}
                              options={ProvincesShippingOptions}
                              value={getOpt(
                                ProvincesShippingOptions,
                                pickup.state
                              )}
                              // isLoading={provincesStatus === 'loading'}
                              onChange={(opt: any) => {
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].state`,
                                  opt?.value ?? ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].district`,
                                  ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].city`,
                                  ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].zip`,
                                  ''
                                );
                                loadDistricts(index, opt?.value ?? '');
                              }}
                              placeholder={t('Select province')}
                              noOptionsMessage={() =>
                                provincesError ?? t('No options')
                              }
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={6}>
                        <Row className={`${RowClass}`}>
                          <Label className="col-sm-3 col-form-label">
                            {t('District')}
                          </Label>
                          <Col sm={9}>
                            <Select
                              isDisabled={!pickup.state}
                              styles={CustomReactSelectStyles(isDarkMode)}
                              options={locationOptions[index]?.districts ?? []}
                              value={
                                locationOptions[index]?.districts.find(
                                  (o) => o.value === pickup.district
                                ) || null
                              }
                              onChange={(opt: any) => {
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].district`,
                                  opt?.value ?? ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].city`,
                                  ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].zip`,
                                  ''
                                );

                                loadSubDistricts(index, opt?.value ?? '');
                              }}
                              placeholder={t('Select district')}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col lg={6}>
                        <Row>
                          <Label className="col-sm-3 col-form-label">
                            {t('Sub-district')}
                          </Label>

                          <Col sm={9}>
                            <Select
                              isDisabled={!pickup.district}
                              styles={CustomReactSelectStyles(isDarkMode)}
                              options={
                                locationOptions[index]?.subDistricts ?? []
                              }
                              value={
                                locationOptions[index]?.subDistricts.find(
                                  (o) => o.value === pickup.city
                                ) || null
                              }
                              onChange={(opt: any) => {
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].city`,
                                  opt?.value ?? ''
                                );
                                formik.setFieldValue(
                                  `pickupAddresses[${index}].zip`,
                                  opt?.zip ?? ''
                                );
                              }}
                              placeholder={t('Select sub-district')}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col lg={6}>
                        <Row className={`${RowClass}`}>
                          <Label className="col-sm-3 col-form-label">
                            {t('Postal Code')}
                          </Label>
                          <Col sm={9}>
                            <Input
                              disabled
                              name={`pickupAddresses[${index}].zip`}
                              value={pickup.zip}
                              onChange={formik.handleChange}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        md={12}
                        className="d-flex align-items-end justify-content-end"
                      >
                        <Button
                          className={`mx-2 ${isDefault ? 'd-none' : ''}`}
                          type="button"
                          color="info"
                          outline
                          onClick={() => {
                            const updated = formik.values.pickupAddresses.map(
                              (item, i) => ({
                                ...item,
                                isDefault: i === index,
                              })
                            );
                            formik.setFieldValue('pickupAddresses', updated);
                          }}
                        >
                          {t('Set as Default')}
                        </Button>
                        <Button
                          type="button"
                          color="danger"
                          outline
                          onClick={async () => {
                            if (pickup.id) {
                              handleDeletePickupAddr(pickup);
                            }
                            arrayHelpers.remove(index);
                          }}
                        >
                          {t('Delete')}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              );
            })}
            <div className="mt-3">
              <Button
                type="button"
                color="primary"
                onClick={() =>
                  arrayHelpers.push({
                    pickupName: '',
                    phone: '',
                    address: '',
                    state: '',
                    district: '',
                    city: '',
                    zip: '',
                    isDefault: false,
                  })
                }
              >
                + {t('Add pickup address')}
              </Button>
            </div>
          </>
        )}
      />
    </TabPane>
  );
};

export default PickUpTab;
